/**
 * The CASCADE layout (owner, 2026-10-02: "make the flow running graph more like a cascade graph
 * left to right - with pills that are very descriptive and show the actual prompts").
 *
 * Strictly left → right. A node's COLUMN is its longest-path depth from the start node, so every
 * forward edge points right and a step never sits left of anything that feeds it. Rows within a
 * column are ordered by a barycenter sweep to cut crossings. Back-edges (retry / fix loops that
 * return to an earlier step) are found by a DFS and drawn as arcs UNDER the graph — they never
 * push a node into a later column, which is what made dagre's loops reshuffle the spine.
 *
 * Pure and deterministic: positions depend only on the node ids, their order, and the edges —
 * never on statuses or metas — so a status poll can never move a node. {@link cascadeKey} is the
 * memo key a caller caches the result under.
 *
 * Kept free of React / @xyflow imports so the node:assert harness (tsconfig.test.json) can run it.
 */

/** A node as the layout sees it. `satellite` = drawn in the bottom lane (artifacts), not ranked. */
export interface CascadeNodeIn {
  id: string;
  satellite?: boolean;
}

export interface CascadeEdgeIn {
  id: string;
  source: string;
  target: string;
}

export interface CascadeOptions {
  /** The node every column counts from. Absent / unknown ⇒ the nodes nothing points at. */
  startId?: string | null;
  /** Pill footprint the slots are sized to (the pill's CSS width / max height). */
  nodeWidth?: number;
  nodeHeight?: number;
  /** Horizontal gap between columns (room for edge labels), vertical gap between rows. */
  colGap?: number;
  rowGap?: number;
  /** Bottom-lane (artifact) node footprint. */
  satelliteWidth?: number;
  satelliteHeight?: number;
  /** Barycenter sweeps (each = one left→right + one right→left pass). */
  sweeps?: number;
}

/** How a back-edge is drawn: below `floorY` (the lowest node it passes), on its own `lane`. */
export interface CascadeBackEdge {
  floorY: number;
  lane: number;
}

export interface CascadeLayout {
  /** Top-left corner per node id (React Flow's `position`). */
  positions: Map<string, { x: number; y: number }>;
  /** Column per ranked node id (satellites carry their anchor's column). */
  columns: Map<string, number>;
  /** Back-edges by edge id. Every other edge between ranked nodes points strictly right. */
  backEdges: Map<string, CascadeBackEdge>;
}

export const CASCADE_DEFAULTS = {
  nodeWidth: 340,
  nodeHeight: 240,
  colGap: 160,
  rowGap: 28,
  satelliteWidth: 200,
  satelliteHeight: 64,
  sweeps: 3,
} as const;

/**
 * The memo key: node ids in order, plus edge endpoints. Deliberately blind to `data` — a status,
 * a meta tick or a new prompt chunk leaves it unchanged, so the cached layout is reused.
 */
export function cascadeKey(nodes: ReadonlyArray<CascadeNodeIn>, edges: ReadonlyArray<CascadeEdgeIn>): string {
  return (
    nodes.map((n) => (n.satellite ? `~${n.id}` : n.id)).join("|") +
    "#" +
    edges.map((e) => `${e.id}=${e.source}>${e.target}`).join("|")
  );
}

export function cascadeLayout(
  nodes: ReadonlyArray<CascadeNodeIn>,
  edges: ReadonlyArray<CascadeEdgeIn>,
  options: CascadeOptions = {},
): CascadeLayout {
  const o = { ...CASCADE_DEFAULTS, ...stripUndefined(options) };
  const positions = new Map<string, { x: number; y: number }>();
  const columns = new Map<string, number>();
  const backEdges = new Map<string, CascadeBackEdge>();

  const ranked = nodes.filter((n) => !n.satellite).map((n) => n.id);
  const order = new Map(ranked.map((id, i) => [id, i]));
  const rankedEdges = edges.filter(
    (e) => order.has(e.source) && order.has(e.target),
  );

  // ── 1. back-edges: DFS from the start (then any unvisited node, in input order) ──────────
  const succ = new Map<string, CascadeEdgeIn[]>();
  for (const id of ranked) succ.set(id, []);
  for (const e of rankedEdges) succ.get(e.source)!.push(e);

  const hasIncoming = new Set(rankedEdges.filter((e) => e.source !== e.target).map((e) => e.target));
  const roots: string[] = [];
  if (options.startId && order.has(options.startId)) roots.push(options.startId);
  for (const id of ranked) if (!hasIncoming.has(id) && !roots.includes(id)) roots.push(id);
  for (const id of ranked) if (!roots.includes(id)) roots.push(id); // cycles nothing enters

  const back = new Set<string>();
  const state = new Map<string, 1 | 2>(); // 1 = on the stack, 2 = finished
  for (const root of roots) {
    if (state.has(root)) continue;
    // Iterative DFS: a deep linear flow must not blow the call stack.
    const stack: Array<{ id: string; i: number }> = [{ id: root, i: 0 }];
    state.set(root, 1);
    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      const out = succ.get(top.id)!;
      if (top.i >= out.length) {
        state.set(top.id, 2);
        stack.pop();
        continue;
      }
      const e = out[top.i++];
      const s = state.get(e.target);
      if (s === 1) {
        back.add(e.id); // points at an ancestor on the stack (or itself): a loop
      } else if (s === undefined) {
        state.set(e.target, 1);
        stack.push({ id: e.target, i: 0 });
      }
    }
  }
  const forward = rankedEdges.filter((e) => !back.has(e.id));

  // ── 2. columns: longest path over the forward (acyclic) edges, Kahn in input order ────────
  const indeg = new Map(ranked.map((id) => [id, 0]));
  for (const e of forward) indeg.set(e.target, indeg.get(e.target)! + 1);
  const col = new Map(ranked.map((id) => [id, 0]));
  const queue = ranked.filter((id) => indeg.get(id) === 0);
  const fwdSucc = new Map<string, string[]>();
  const fwdPred = new Map<string, string[]>();
  for (const id of ranked) {
    fwdSucc.set(id, []);
    fwdPred.set(id, []);
  }
  for (const e of forward) {
    fwdSucc.get(e.source)!.push(e.target);
    fwdPred.get(e.target)!.push(e.source);
  }
  for (let qi = 0; qi < queue.length; qi++) {
    const id = queue[qi];
    for (const t of fwdSucc.get(id)!) {
      col.set(t, Math.max(col.get(t)!, col.get(id)! + 1));
      indeg.set(t, indeg.get(t)! - 1);
      if (indeg.get(t) === 0) queue.push(t);
    }
  }
  for (const [id, c] of col) columns.set(id, c);

  // ── 3. rows: initial order = DFS discovery order, then barycenter sweeps ────────────────
  const discovery = new Map<string, number>();
  {
    const seen = new Set<string>();
    for (const root of roots) {
      const stack = [root];
      while (stack.length > 0) {
        const id = stack.pop()!;
        if (seen.has(id)) continue;
        seen.add(id);
        discovery.set(id, discovery.size);
        const next = fwdSucc.get(id)!;
        for (let i = next.length - 1; i >= 0; i--) if (!seen.has(next[i])) stack.push(next[i]);
      }
    }
  }
  const maxCol = ranked.reduce((m, id) => Math.max(m, col.get(id)!), 0);
  const layers: string[][] = Array.from({ length: ranked.length ? maxCol + 1 : 0 }, () => []);
  for (const id of ranked) layers[col.get(id)!].push(id);
  for (const layer of layers) layer.sort((a, b) => discovery.get(a)! - discovery.get(b)!);

  const rowOf = new Map<string, number>();
  const syncRows = () => layers.forEach((layer) => layer.forEach((id, i) => rowOf.set(id, i)));
  syncRows();
  // A row index normalised to its column's height, so a 1-node column and a 5-node column are
  // compared on the same vertical scale (the columns are drawn centred — see step 4).
  const centred = (id: string) => rowOf.get(id)! - (layers[col.get(id)!].length - 1) / 2;
  const sweepBy = (layer: string[], neighbours: (id: string) => string[]) => {
    const keyed = layer.map((id, i) => {
      const ns = neighbours(id);
      const bc = ns.length ? ns.reduce((s, n) => s + centred(n), 0) / ns.length : null;
      return { id, i, bc };
    });
    // Nodes without neighbours on that side keep their slot; stable on ties (input order).
    const movable = keyed.filter((k) => k.bc !== null).sort((a, b) => a.bc! - b.bc! || a.i - b.i);
    let m = 0;
    const out = keyed.map((k) => (k.bc === null ? k.id : movable[m++].id));
    layer.splice(0, layer.length, ...out);
  };
  for (let s = 0; s < o.sweeps; s++) {
    for (let c = 1; c < layers.length; c++) {
      sweepBy(layers[c], (id) => fwdPred.get(id)!);
      syncRows();
    }
    for (let c = layers.length - 2; c >= 0; c--) {
      sweepBy(layers[c], (id) => fwdSucc.get(id)!);
      syncRows();
    }
  }

  // ── 4. coordinates: columns left → right, each column centred on y = 0 ────────────────
  const stepX = o.nodeWidth + o.colGap;
  const stepY = o.nodeHeight + o.rowGap;
  let bottom = 0;
  for (let c = 0; c < layers.length; c++) {
    const layer = layers[c];
    const top = -((layer.length - 1) * stepY) / 2;
    layer.forEach((id, i) => {
      const y = top + i * stepY;
      positions.set(id, { x: c * stepX, y });
      bottom = Math.max(bottom, y + o.nodeHeight);
    });
  }

  // ── 5. satellites (artifacts): a bottom lane, under the column of the node they hang off ──
  const satellites = nodes.filter((n) => n.satellite).map((n) => n.id);
  if (satellites.length > 0) {
    const anchorOf = (sid: string): string | undefined => {
      // The producer (an edge INTO the satellite) wins; else its first consumer.
      const prod = edges.find((e) => e.target === sid && order.has(e.source));
      if (prod) return prod.source;
      return edges.find((e) => e.source === sid && order.has(e.target))?.target;
    };
    const perCol = new Map<number, number>();
    const laneTop = bottom + o.rowGap * 2;
    for (const sid of satellites) {
      const anchor = anchorOf(sid);
      const c = anchor !== undefined ? col.get(anchor)! : 0;
      const k = perCol.get(c) ?? 0;
      perCol.set(c, k + 1);
      columns.set(sid, c);
      positions.set(sid, {
        x: c * stepX + (o.nodeWidth - o.satelliteWidth) / 2,
        y: laneTop + k * (o.satelliteHeight + o.rowGap / 2),
      });
    }
  }

  // ── 6. back-edge routing: below the lowest node in the span, one lane per overlap ─────────
  const backList = rankedEdges
    .filter((e) => back.has(e.id))
    .map((e) => {
      const a = col.get(e.target)!;
      const b = col.get(e.source)!;
      return { e, lo: Math.min(a, b), hi: Math.max(a, b) };
    })
    .sort((x, y) => x.hi - x.lo - (y.hi - y.lo) || x.lo - y.lo || order.get(x.e.source)! - order.get(y.e.source)!);
  const placed: Array<{ lo: number; hi: number; lane: number }> = [];
  for (const { e, lo, hi } of backList) {
    let floorY = -Infinity;
    for (let c = lo; c <= hi; c++) {
      for (const id of layers[c]) floorY = Math.max(floorY, positions.get(id)!.y + o.nodeHeight);
    }
    // Shorter loops nest inside longer ones: the lane is one deeper than any overlapping arc.
    const lane = placed
      .filter((p) => p.lo <= hi && lo <= p.hi)
      .reduce((m, p) => Math.max(m, p.lane + 1), 0);
    placed.push({ lo, hi, lane });
    backEdges.set(e.id, { floorY, lane });
  }

  return { positions, columns, backEdges };
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of Object.keys(o) as Array<keyof T>) {
    if (o[k] !== undefined) out[k] = o[k];
  }
  return out;
}
